import React, { useEffect, useRef, useState } from "react";
import { Palette, UserRound, Check } from "lucide-react";
import { Button, Dialog } from "@/components/ui";
import { cn } from "@/lib/cn";
import { useAppearance } from "@/theme/AppearanceProvider";
import type { ColorMode } from "@/theme/appearance";
import { useAuthedApi } from "@/api/useAuthedApi";
import { AppearancePanel } from "./AppearancePanel";
import { ProfilePanel } from "./ProfilePanel";

export type SectionId = "appearance" | "profile";

const SECTIONS: { id: SectionId; label: string; icon: React.ReactNode }[] = [
  { id: "appearance", label: "Appearance", icon: <Palette size={16} /> },
  { id: "profile", label: "Profile", icon: <UserRound size={16} /> },
];

/** One Settings workspace opened from the account menu. Appearance and Profile
 *  live as tabs in the left rail.
 *
 *  Appearance previews live on the app and is committed via the footer Save
 *  (which keeps the modal open) or reverted via Discard. While there are
 *  unsaved appearance changes the other tabs are locked, so the user can't
 *  wander off with a half-applied theme. Profile has its own inline per-group
 *  saves, so the footer only shows on the Appearance tab. */
export function SettingsModal({
  open,
  onClose,
}: {
  open: boolean;
  onClose: () => void;
}) {
  const [active, setActive] = useState<SectionId>("appearance");
  const [saved, setSaved] = useState(false);
  const { colorMode, accentLight, accentDark, setColorMode, setAccents } = useAppearance();
  const { profile } = useAuthedApi();

  // The last committed selection: the snapshot on open, advanced on every Save.
  // Appearance is "dirty" whenever the live selection differs from it. Both
  // per-mode accents are tracked so a change to either (across a mode switch
  // inside the modal) counts.
  const committed = useRef<{ colorMode: ColorMode; accentLight: string; accentDark: string } | null>(null);
  useEffect(() => {
    if (open) {
      committed.current = { colorMode, accentLight, accentDark };
      setActive("appearance");
      setSaved(false);
    }
    // Snapshot only on open — not as selections change.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const dirty =
    committed.current !== null &&
    (committed.current.colorMode !== colorMode ||
      committed.current.accentLight !== accentLight ||
      committed.current.accentDark !== accentDark);

  // Any new tweak invalidates the "Saved" acknowledgement.
  useEffect(() => {
    if (dirty) setSaved(false);
  }, [dirty]);

  const revert = () => {
    const c = committed.current;
    if (!c) return;
    if (c.colorMode !== colorMode) setColorMode(c.colorMode);
    if (c.accentLight !== accentLight || c.accentDark !== accentDark) {
      setAccents({ accentLight: c.accentLight, accentDark: c.accentDark });
    }
  };

  const save = () => {
    // Only the ACCENTS persist server-side (for cross-device sync). The color
    // mode (light/dark/system) is a per-device client preference kept in
    // localStorage via the provider effect — we never send it to the server.
    void profile.update({ appearance: { accentLight, accentDark } }).catch(() => {});
    committed.current = { colorMode, accentLight, accentDark };
    setSaved(true);
  };

  // X / Escape / backdrop: drop any unsaved appearance preview, then close.
  const closeModal = () => {
    revert();
    onClose();
  };

  const selectTab = (id: SectionId) => {
    if (dirty) return; // locked until saved or discarded
    setActive(id);
  };

  return (
    <Dialog open={open} onClose={closeModal} title="Settings" size="lg">
      <div className="flex h-full min-h-0 flex-col">
        <div className="flex flex-1 min-h-0">
          <nav className="w-48 shrink-0 border-r border-hairline px-3 py-2 bg-sidebar/50">
            {SECTIONS.map((s) => {
              const locked = dirty && s.id !== active;
              return (
                <button
                  key={s.id}
                  type="button"
                  onClick={() => selectTab(s.id)}
                  disabled={locked}
                  title={locked ? "Save or discard your appearance changes first" : undefined}
                  className={cn(
                    "flex w-full items-center gap-2.5 rounded-[10px] px-3 py-2 text-[13.5px]",
                    s.id === active
                      ? "bg-nav-active text-ink font-semibold shadow-sm"
                      : "text-ink-secondary font-medium hover:bg-hover",
                    locked ? "opacity-40 cursor-not-allowed" : "cursor-pointer",
                  )}
                >
                  <span className="text-nav-icon">{s.icon}</span>
                  {s.label}
                </button>
              );
            })}
          </nav>
          <div className="flex-1 min-w-0 overflow-y-auto">
            {active === "appearance" && <AppearancePanel />}
            {active === "profile" && <ProfilePanel />}
          </div>
        </div>
        {active === "appearance" && (
          <footer className="flex items-center justify-end gap-3 px-6 py-4 border-t border-hairline shrink-0">
            {dirty ? (
              <span className="mr-auto text-[12.5px] text-ink-muted">
                Unsaved changes. Save or discard to switch tabs.
              </span>
            ) : saved ? (
              <span className="mr-auto inline-flex items-center gap-1.5 text-[13px] font-medium text-accent">
                <Check size={15} strokeWidth={2.6} /> Saved
              </span>
            ) : null}
            <Button variant="secondary" onClick={revert} disabled={!dirty}>
              Discard
            </Button>
            <Button onClick={save} disabled={!dirty}>
              Save preferences
            </Button>
          </footer>
        )}
      </div>
    </Dialog>
  );
}
